import type {
  CierreClientes,
  CorreoClientes,
  FuenteClientesNuevos,
} from '../types/indicadores.types';
import { fmtInt, fmtMoney } from './format';

/**
 * Las sub-líneas de los datos «al cierre» y de los tickets (SPEC 2026-10-13,
 * puntos c y d). Puras, para probarlas sin montar las tarjetas.
 */

/** Procedencia de las altas de un periodo, tal como la manda la API. */
export interface ProcedenciaClientes {
  fuente: FuenteClientesNuevos;
  cierre: CierreClientes | null;
  /** Altas del snapshot − altas del cierre (lo que el ERP corrigió después). */
  deriva: number | null;
  /** «El correo dijo …» de semanas importadas (sólo sin cascada, es global). */
  correo: CorreoClientes | null;
}

const DIAS_SEMANA = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

/**
 * Instante ISO -> «lun 05/10 08:00» en hora de MÉXICO, sin importar la zona
 * del navegador (el correo sale a las 08:00 de México). `null` si no se lee.
 */
export function fmtInstanteCierre(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Mexico_City',
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const p = (t: string) => partes.find((x) => x.type === t)?.value ?? '';
  const dia = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p('weekday'));
  // `hour12: false` puede dar «24» a medianoche en algunos motores.
  const hora = p('hour') === '24' ? '00' : p('hour');
  return `${DIAS_SEMANA[dia] ?? ''} ${p('day')}/${p('month')} ${hora}:${p('minute')}`.trim();
}

/** Días `YYYY-MM-DD` de [desde, hasta], inclusive. */
function dias(desde: string, hasta: string): string[] {
  const out: string[] = [];
  const d = new Date(`${desde}T12:00:00Z`);
  const fin = new Date(`${hasta}T12:00:00Z`);
  while (d.getTime() <= fin.getTime()) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** Cuántos días de [desde, hasta] caen en las semanas (lunes) del cierre. */
export function diasAlCierre(
  semanas: string[],
  desde: string,
  hasta: string,
): { de: number; total: number } {
  const delCierre = new Set(semanas.flatMap((l) => {
    const d = new Date(`${l}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 6);
    return dias(l, d.toISOString().slice(0, 10));
  }));
  const todos = dias(desde, hasta);
  return { de: todos.filter((x) => delCierre.has(x)).length, total: todos.length };
}

/**
 * La sub-línea de procedencia de «Clientes nuevos»:
 * - `cierre`: «al cierre del lun 05/10 08:00» y, si hay deriva, «el ERP hoy:
 *   1,085 (+7 corregidas después)»;
 * - `mixta`: «N de M días al cierre»;
 * - `calculo`: «a la fecha de cálculo» y, si el periodo es de semanas
 *   importadas, «el correo dijo 1,078».
 * `null` contra una API anterior (sin `fuente`): no se pinta nada nuevo.
 */
export function lineaProcedenciaClientes(
  p: ProcedenciaClientes | null | undefined,
  altas: number | null,
  rango: { desde: string; hasta: string } | null,
): string | null {
  if (!p) return null;
  if (p.fuente === 'cierre' && p.cierre) {
    const cuando = fmtInstanteCierre(p.cierre.generadoEn);
    const base =
      p.cierre.semanas.length > 1
        ? `al cierre de ${p.cierre.semanas.length} semanas`
        : `al cierre${cuando ? ` del ${cuando}` : ''}`;
    if (p.deriva && altas !== null) {
      const signo = p.deriva > 0 ? '+' : '−';
      const n = Math.abs(p.deriva);
      return `${base} · el ERP hoy: ${fmtInt(altas + p.deriva)} (${signo}${fmtInt(n)} ${n === 1 ? 'corregida' : 'corregidas'} después)`;
    }
    return base;
  }
  if (p.fuente === 'mixta' && p.cierre && rango) {
    const { de, total } = diasAlCierre(p.cierre.semanas, rango.desde, rango.hasta);
    return `${de} de ${total} días al cierre`;
  }
  const base = 'a la fecha de cálculo';
  return p.correo ? `${base} · el correo dijo ${fmtInt(p.correo.altas)}` : base;
}

/**
 * La sub-línea de tickets de «Venta total» (SPEC 2026-10-13, punto d):
 * «37,825 tickets · ticket promedio $797», con el promedio = venta ÷ (tickets −
 * tickets de préstamo): la venta ya no incluye los préstamos. `undefined` (API
 * anterior) -> `null`, no se pinta; `null` -> «tickets N/D».
 */
export function lineaTickets(
  tickets: number | null | undefined,
  ticketsPrestamo: number | null | undefined,
  ventaMonto: number,
): string | null {
  if (tickets === undefined) return null;
  if (tickets === null) return 'tickets N/D';
  const base = `${fmtInt(tickets)} ${tickets === 1 ? 'ticket' : 'tickets'}`;
  const sinPrestamos = tickets - (ticketsPrestamo ?? 0);
  return sinPrestamos > 0
    ? `${base} · ticket promedio ${fmtMoney(ventaMonto / sinPrestamos)}`
    : base;
}
